import type { EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useGetGuestSessionStateQuery } from './GuestSessionState.graphql.generated';
import { sessionHeaderContext } from '@shared/core/apollo/sessionHeaderContext';

export type UseGuestSessionStateArgs = Readonly<{
  eventHandle: string | undefined | null;
  skip?: boolean;
}>;

export type UseGuestSessionStateReturn = Readonly<{
  /** The identified guest, `null` when the session knows nobody, `undefined` when unread. */
  person: Readonly<{ fullName?: string | null; email?: string | null }> | null | undefined;
  /** The guest's RSVP attendance status, or `undefined` while unread. */
  rsvpAttendanceStatus: EventRSVPAttendanceStatus | undefined;
  /**
   * Whether each half has actually been delivered. Tracked separately because the cache
   * routinely holds one without the other — see `returnPartialData` below.
   */
  hasDeliveredPerson: boolean;
  loading: boolean;
  isSkipped: boolean;
}>;

/**
 * One read of the viewing guest's event session, shared by everything that needs part of
 * it — who they are, and whether they said they're coming.
 *
 * **Why a single document.** Both facts live on the same `activeSession`, and both proxy
 * the legacy Service API (~750ms). Apollo only dedupes identical documents, so asking for
 * them separately costs two round trips on pages that need both — registry checkout needs
 * the identity for the details form and the attendance for the post-purchase redirect.
 *
 * Read client-side (`ssr: false`) with `cache-and-network`: the server render can cache
 * the anonymous `activeSession`, and cache-first would replay that stale result for an
 * identified guest. The session is forwarded by hand via `sessionHeaderContext` because
 * the `joy_session_id` cookie only reaches the gateway when the page is same-origin —
 * on a masked or custom domain, or in an iframe, it is never sent.
 *
 * Prefer the focused hooks over this one: `useGuestSessionIdentity` for name and email,
 * `useRsvpAttendanceStatus` for attendance. They apply the resolution semantics each
 * question needs, which differ.
 */
export const useGuestSessionState = ({ eventHandle, skip }: UseGuestSessionStateArgs): UseGuestSessionStateReturn => {
  const isSkipped = Boolean(skip) || !eventHandle;

  const { data, loading } = useGetGuestSessionStateQuery({
    variables: { eventHandle: eventHandle || '' },
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    // Required, not incidental. Asking for two fields in one document means a cache entry
    // holding only one of them satisfies neither: the default withholds the whole result.
    // That is the common case here — the registry guest NavBar caches `activeSession.person`
    // with no `eventCTA`, so without this, merging the documents would cost identity the
    // instant cache hit it used to get and make every prefill wait on the network.
    returnPartialData: true,
    context: sessionHeaderContext(),
    skip: isSkipped,
    ssr: false,
  });

  const activeSession = data?.eventByName?.activeSession;

  return {
    person: activeSession?.person,
    rsvpAttendanceStatus: activeSession?.eventCTA?.rsvpAttendanceStatus ?? undefined,
    // `null` counts as delivered — the session answered, and it knows nobody. Only
    // `undefined` means the field was absent from a partial read.
    hasDeliveredPerson: activeSession?.person !== undefined,
    loading,
    isSkipped,
  };
};
